import { Modal } from "antd";
import clsx from "clsx";
import styled from "styled-components";
import "./Noti.style.scss";

type P_Props = {
  title: string;
  subTitle?: string;
  visible: boolean;
  actions?: any;
  icon?: {
    color?: "yellow" | "red" | "purple" | "blue" | "browse";
    size?: number;
    children?: any;
  };
};

const StyledPulse = styled.div((props: { pulseColor?: string; size?: number; className: string }) => ({
  width: "fit-content",
  height: "fit-content",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [`&.${props.pulseColor}`]: {
    backgroundColor: `${props.pulseColor}`,
    animation: `pulse-${props.pulseColor} 2s linear infinite`,
  },
  [`@keyframes pulse-green`]: {
    "0%": {
      boxShadow: `0 0 0 0 rgba(228, 255, 227, 1), 0 0 0 0 rgba(228, 255, 227, 1)`,
    },
    "40%": {
      boxShadow: `0 0 0 ${props.size}px rgba(228, 255, 227, 0.4), 0 0 0 0 rgba(228, 255, 227, 0.4)`,
    },
    "80%": {
      boxShadow: `0 0 0 ${props.size}px rgba(228, 255, 227, 0), 0 0 0 5px rgba(228, 255, 227, 0)`,
    },
    "100%": {
      boxShadow: `0 0 0 0 rgba(228, 255, 227, 0), 0 0 0 5px rgba(228, 255, 227, 0)`,
    },
  },
  [`@keyframes pulse-purple`]: {
    "0%": {
      boxShadow: `0 0 0 0 rgba(248, 247, 255, 1), 0 0 0 0 rgba(248, 247, 255, 1)`,
    },
    "40%": {
      boxShadow: `0 0 0 ${props.size}px rgba(248, 247, 255, 0.8), 0 0 0 0 rgba(248, 247, 255, 0.8)`,
    },
    "80%": {
      boxShadow: `0 0 0 ${props.size}px rgba(248, 247, 255, 0.6), 0 0 0 5px rgba(248, 247, 255, 0.6)`,
    },
    "100%": {
      boxShadow: `0 0 0 0 rgba(248, 247, 255, 0), 0 0 0 5px rgba(248, 247, 255, 0)`,
    },
  },
  [`@keyframes pulse-yellow`]: {
    "0%": {
      boxShadow: `0 0 0 0 rgba(255, 245, 220, 1), 0 0 0 0 rgba(255, 245, 220, 1)`,
    },
    "40%": {
      boxShadow: `0 0 0 ${props.size}px rgba(255, 245, 220, 0.4), 0 0 0 0 rgba(255, 245, 220, 0.4)`,
    },
    "80%": {
      boxShadow: `0 0 0 ${props.size}px rgba(255, 245, 220, 0), 0 0 0 5px rgba(255, 245, 220, 0)`,
    },
    "100%": {
      boxShadow: `0 0 0 0 rgba(255, 245, 220, 0), 0 0 0 5px rgba(255, 245, 220, 0)`,
    },
  },
  [`@keyframes pulse-browse`]: {
    "0%": {
      boxShadow: `0 0 0 0 rgba(255, 229, 205 1), 0 0 0 0 rgba(255, 229, 205 1)`,
    },
    "40%": {
      boxShadow: `0 0 0 ${props.size}px rgba(255, 229, 205, 0.4), 0 0 0 0 rgba(255, 229, 205, 0.4)`,
    },
    "80%": {
      boxShadow: `0 0 0 ${props.size}px rgba(255, 229, 205, 0), 0 0 0 5px rgba(255, 229, 205, 0)`,
    },
    "100%": {
      boxShadow: `0 0 0 0 rgba(255, 229, 205, 0), 0 0 0 5px rgba(255, 229, 205, 0)`,
    },
  },
  [`@keyframes pulse-red`]: {
    "0%": {
      boxShadow: `0 0 0 0 rgba(255, 241, 240, 1), 0 0 0 0 rgba(255, 241, 240, 1)`,
    },
    "40%": {
      boxShadow: `0 0 0 ${props.size}px rgba(255, 241, 240, 0.4), 0 0 0 0 rgba(255, 241, 240, 0.4)`,
    },
    "80%": {
      boxShadow: `0 0 0 ${props.size}px rgba(255, 241, 240, 0), 0 0 0 5px rgba(255, 241, 240, 0)`,
    },
    "100%": {
      boxShadow: `0 0 0 0 rgba(255, 241, 240, 0), 0 0 0 5px rgba(255, 241, 240, 0)`,
    },
  },

  [`@keyframes pulse-blue`]: {
    "0%": {
      boxShadow: `0 0 0 0 rgba(64, 153, 237, 1), 0 0 0 0 rgba(64, 153, 237, 1)`,
    },
    "40%": {
      boxShadow: `0 0 0 ${props.size}px rgba(64, 153, 237, 0.4), 0 0 0 0 rgba(64, 153, 237, 0.4)`,
    },
    "80%": {
      boxShadow: `0 0 0 ${props.size}px rgba(64, 153, 237, 0), 0 0 0 5px rgba(64, 153, 237, 0)`,
    },
    "100%": {
      boxShadow: `0 0 0 0 rgba(64, 153, 237, 0), 0 0 0 5px rgba(64, 153, 237, 0)`,
    },
  },
}));

const NotiPresenter = (props: P_Props) => {
  return (
    <Modal className="noti" closable={false} open={props.visible} footer={null} destroyOnClose width={520}>
      <div className="noti__inner">
        <div className="noti__inner__icon">
          {props.icon && (
            <StyledPulse
              pulseColor={props.icon.color}
              size={props.icon.size || 24}
              className={clsx({
                [props.icon.color || ""]: true,
              })}
            >
              {props.icon.children}
            </StyledPulse>
          )}
        </div>
        <div className="noti__inner__title">{props.title}</div>
        <div className="noti__inner__sub-title">{props.subTitle}</div>
        <div className="noti__inner__actions">{props.actions}</div>
      </div>
    </Modal>
  );
};

export default NotiPresenter;
